<template>
  <wc-data-box
    title='Attachments'
    titleIcon='mdi-paperclip'
    titleIconColor='orange'
    :showAdd='!isBusy'
    :loading='isBusy'
    @add='openPicker'
  >
    <input
      ref='picker'
      type='file'
      class='d-none'
      :accept='accept'
      @change='onFileChosen'
    />

    <v-alert v-if='error' dense text type='error' dismissible class='ma-2' @input='error = null'>{{ error }}</v-alert>

    <v-simple-table>
      <tbody>
        <tr v-for='file in attachments' :key='file.id'>
          <td width='40'><v-icon :color='iconFor(file).color'>{{ iconFor(file).icon }}</v-icon></td>
          <td>
            <a :href='file.url' target='_blank' rel='noopener'>{{ file.name }}</a>
          </td>
          <td class='text-right text-no-wrap'>{{ formatSize(file.size) }}</td>
          <td class='text-right' width='60'>
            <v-btn icon small :disabled='isBusy' @click='fileToDelete = file'>
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
        <tr v-if='!attachments.length'>
          <td colspan='4' class='grey--text'>No files attached.</td>
        </tr>
        <tr v-if='attachments.length'>
          <td colspan='2' class='text-right font-weight-medium'>Total</td>
          <td class='text-right text-no-wrap font-weight-medium'>{{ formatSize(totalSize) }} / {{ formatSize(maxTotalBytes) }}</td>
          <td></td>
        </tr>
      </tbody>
    </v-simple-table>
    <div class='caption grey--text ma-2'>
      Attached to every email sent with this notice. Allowed: PDF, Word, Excel. Max {{ formatSize(maxFileBytes) }} per file, {{ formatSize(maxTotalBytes) }} in total.
    </div>

    <wc-confirm
      :showDialog='!!fileToDelete'
      saveText='Delete'
      @cancel='fileToDelete = null'
      @confirm='removeFile'
    >
      Delete <b>{{ fileToDelete && fileToDelete.name }}</b>? It will no longer be attached to emails.
    </wc-confirm>
  </wc-data-box>
</template>

<script>
import noticesAPI from '@/modules/notices/api/'

const EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx']
const MB = 1024 * 1024

export default {
  name: 'notice-attachments',
  props: {
    noticeId: {
      required: true
    },
    attachments: {
      required: true,
      type: Array
    }
  },
  data: () => ({
    isBusy: false,
    error: null,
    fileToDelete: null,
    accept: EXTENSIONS.map(e => '.' + e).join(','),
    maxFileBytes: 5 * MB,
    maxTotalBytes: 8 * MB
  }),
  computed: {
    totalSize () {
      return this.attachments.reduce((sum, f) => sum + f.size, 0)
    }
  },
  methods: {
    openPicker () {
      this.$refs.picker.click()
    },
    extensionOf (name) {
      return name.includes('.') ? name.split('.').pop().toLowerCase() : ''
    },
    iconFor (file) {
      const ext = this.extensionOf(file.name)
      if (ext === 'pdf') return { icon: 'mdi-file-pdf-box', color: 'red' }
      if (ext === 'doc' || ext === 'docx') return { icon: 'mdi-file-word-box', color: 'blue' }
      if (ext === 'xls' || ext === 'xlsx') return { icon: 'mdi-file-excel-box', color: 'green' }
      return { icon: 'mdi-file', color: 'grey' }
    },
    formatSize (bytes) {
      if (bytes >= MB) return (bytes / MB).toFixed(1) + ' MB'
      return Math.max(1, Math.round(bytes / 1024)) + ' KB'
    },
    validate (file) {
      if (!EXTENSIONS.includes(this.extensionOf(file.name))) {
        return 'Only PDF, Word (doc, docx) and Excel (xls, xlsx) files are allowed.'
      }
      if (file.size === 0) return 'The selected file is empty.'
      if (file.size > this.maxFileBytes) return `File exceeds the ${this.formatSize(this.maxFileBytes)} limit.`
      if (this.totalSize + file.size > this.maxTotalBytes) {
        return `Total attachments would exceed ${this.formatSize(this.maxTotalBytes)}.`
      }
      return null
    },
    async onFileChosen (event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file) return

      this.error = this.validate(file)
      if (this.error) return

      this.isBusy = true
      let created = null
      try {
        created = await noticesAPI.createAttachment(this.noticeId, file.name, file.size)
        await noticesAPI.uploadAttachment(created.url, file)
        this.$emit('added', created.file)
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.detail) || 'Upload failed. Please try again.'
        if (created) {
          // Don't leave a record pointing at a file that never reached S3
          await noticesAPI.deleteAttachment(this.noticeId, created.file.id).catch(() => {})
        }
      } finally {
        this.isBusy = false
      }
    },
    async removeFile () {
      const file = this.fileToDelete
      this.fileToDelete = null
      this.isBusy = true
      try {
        await noticesAPI.deleteAttachment(this.noticeId, file.id)
        this.$emit('removed', file.id)
      } catch (err) {
        this.error = 'Could not delete the file. Please try again.'
      } finally {
        this.isBusy = false
      }
    }
  }
}
</script>
